import { IGif } from '@giphy/js-types'

/**
 * Appends the 'ap=1' query parameter to a given URL.
 * This is used to indicate that the asset is being used in an ad.
 * @param url The URL to modify.
 * @returns The modified URL with the 'ap=1' query parameter.
 */
export const appendApParam = (url: string) => {
    try {
        const u = new URL(url)
        // Avoid duplicating if already present
        if (!u.searchParams.has('ap')) {
            u.searchParams.append('ap', '1')
        }
        return u.toString()
    } catch {
        // Fallback for non-standard URLs
        const hasQuery = url.includes('?')
        const hasAp = /[?&]ap=1(?!\d)/.test(url)
        return hasAp ? url : `${url}${hasQuery ? '&' : '?'}ap=1`
    }
}

/**
 * Processes a GIF's images to append the 'ap=1' parameter to all image URLs.
 * This is used for ad tracking.
 * @param gif The GIF object to process.
 * @param appendAp Whether to append the ap=1 parameter (default: true)
 */
export const processGifImages = (gif?: IGif, appendAp: boolean = true) => {
    if (!gif?.images) return
    if (!appendAp) return

    Object.values(gif.images).forEach((image) => {
        if (!image || typeof image !== 'object') return
        Object.entries(image).forEach(([key, value]) => {
            if (typeof value === 'string' && /^https?:\/\//.test(value)) {
                ;(image as Record<string, unknown>)[key] = appendApParam(value)
            }
        })
    })
}

/**
 * Processes a GIF's video assets to append the 'ap=1' parameter to all asset URLs.
 * This is used for ad tracking.
 * @param gif The GIF object to process.
 * @param appendAp Whether to append the ap=1 parameter (default: true)
 */
export const processGifVideoAssets = (gif?: IGif, appendAp: boolean = true) => {
    if (!gif?.video?.assets) return
    if (!appendAp) return

    Object.values(gif.video.assets).forEach((asset) => {
        if (!asset || typeof asset !== 'object') return

        Object.entries(asset).forEach(([key, value]) => {
            if (typeof value === 'string' && /^https?:\/\//.test(value)) {
                ;(asset as Record<string, unknown>)[key] = appendApParam(value)
            }
        })
    })
}
